import { Mail, Phone, MapPin, MessageCircle, Warehouse } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { FOOTER } from "@/constants/testIds";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_intl-food-commerce/artifacts/zs0n14da_LOGO%20FOOD.png";

const Footer = () => {
  const { t } = useLang();
  const year = new Date().getFullYear();

  const exploreLinks = [
    { id: "about", label: t.nav.about, route: null },
    { id: "services", label: t.nav.services, route: null },
    { id: "products", label: t.nav.products, route: null },
    { id: "markets", label: t.nav.markets, route: null },
    { id: "news", label: t.nav.news, route: "/news" },
    { id: "faq", label: t.nav.faq, route: null },
  ];

  const go = (id, route) => (e) => {
    e.preventDefault();
    if (route) {
      window.location.href = route;
      return;
    }
    if (window.location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer data-testid={FOOTER.root} className="bg-[#FAFAF8] border-t border-[#E2E2DB] pt-20 pb-10">
      <div className="container-cust grid lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <img
              src={LOGO_URL}
              alt="Food & Beverage Services"
              className="h-14 w-14 logo-blend object-contain"
            />
            <div className="flex flex-col leading-tight">
              <span className="font-serif-display text-base text-[#0E5A4E] font-medium">
                Food &amp; Beverage
              </span>
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#4A5D58]">
                Services S.R.L.
              </span>
            </div>
          </div>
          <p className="text-[#4A5D58] text-sm leading-relaxed max-w-sm">
            {t.footer.tagline}
          </p>
        </div>

        <div className="lg:col-span-3">
          <div className="text-xs uppercase tracking-widest text-[#0E5A4E] mb-5">
            {t.footer.explore}
          </div>
          <ul className="space-y-3">
            {exploreLinks.map((l) => (
              <li key={l.id}>
                <a
                  href={l.route || `#${l.id}`}
                  onClick={go(l.id, l.route)}
                  className="text-sm text-[#1A2421] hover:text-[#0E5A4E]"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-4">
          <div className="text-xs uppercase tracking-widest text-[#0E5A4E] mb-5">
            {t.footer.contact}
          </div>
          <ul className="space-y-4 text-sm text-[#1A2421]">
            {t.contact.addresses.map((addr, i) => {
              const Icon = i === 0 ? MapPin : Warehouse;
              return (
                <li key={i} className="flex items-start gap-3">
                  <Icon className="w-4 h-4 mt-0.5 text-[#0E5A4E] flex-shrink-0" />
                  <span>
                    <span className="block text-[10px] uppercase tracking-widest text-[#4A5D58] mb-0.5">
                      {addr.label}
                    </span>
                    {addr.value}
                  </span>
                </li>
              );
            })}
            <li className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-[#0E5A4E]" />
              <a
                href={`tel:${t.contact.phoneValue.replace(/\s+/g, "")}`}
                className="hover:text-[#0E5A4E]"
              >
                {t.contact.phoneValue}
              </a>
              <a
                href={`https://wa.me/${t.contact.phoneValue.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-[#0E5A4E] hover:underline"
              >
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-[#0E5A4E] flex-shrink-0" />
              <a
                href={`mailto:${t.contact.emailFooter}`}
                className="hover:text-[#0E5A4E] break-all"
              >
                {t.contact.emailFooter}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="container-cust pt-8 border-t border-[#E2E2DB] flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-[#4A5D58]">
        <div>
          © {year} Food &amp; Beverage Services S.R.L. — {t.footer.rights} · {t.footer.pIva}
        </div>
        <div className="flex gap-5">
          <a href="#privacy" className="hover:text-[#0E5A4E]">
            {t.footer.privacy}
          </a>
          <a href="#terms" className="hover:text-[#0E5A4E]">
            {t.footer.terms}
          </a>
          <a href="#cookies" className="hover:text-[#0E5A4E]">
            {t.footer.cookie}
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
